import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../utils/apiConfig';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Send, Users, MessageSquare, IndianRupee, CheckCircle2, XCircle,
  Loader2, Trash2, Eye, PhoneCall, AlertCircle, Megaphone,
} from 'lucide-react';

// ── helpers ──────────────────────────────────────────────────────────────────

function cleanPhone(raw) {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return digits;
  if (digits.length === 13 && digits.startsWith('091')) return digits.slice(1);
  return '';
}

function parseContacts(raw) {
  const tokens = raw.split(/[\n,;]+/);
  const seen = new Set();
  const valid = [];
  let invalid = 0;
  for (const tok of tokens) {
    const t = tok.trim();
    if (!t) continue;
    const phone = cleanPhone(t);
    if (phone && !seen.has(phone)) {
      seen.add(phone);
      valid.push(phone);
    } else if (!phone) {
      invalid++;
    }
  }
  return { valid, invalid, duplicates: tokens.filter(Boolean).length - valid.length - invalid };
}

function statusBadge(status) {
  const map = {
    sending: { label: 'Sending…', cls: 'bg-blue-100 text-blue-700' },
    completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
    partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-700' },
    failed: { label: 'Failed', cls: 'bg-rose-100 text-rose-700' },
    draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600' },
  };
  const s = map[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
}

function contactStatusBadge(status) {
  const map = {
    queued: { label: 'Queued', cls: 'bg-slate-100 text-slate-600' },
    sent: { label: 'Sent', cls: 'bg-emerald-100 text-emerald-700' },
    failed: { label: 'Failed', cls: 'bg-rose-100 text-rose-700' },
    delivered: { label: 'Delivered', cls: 'bg-blue-100 text-blue-700' },
    read: { label: 'Read', cls: 'bg-violet-100 text-violet-700' },
  };
  const s = map[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
}

const fmtTs = (t) => (t ? new Date(t).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const Pill = ({ cls, label, title, testid }) => (
  <span title={title} data-testid={testid} className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{label}</span>
);

// Delivery status strictly from provider callbacks — never inferred
function deliveryBadge(c, i) {
  const ds = (c.delivery_status || '').toLowerCase();
  const tid = `wac-delivery-${i}`;
  if (c.status === 'failed') return <Pill cls="bg-rose-100 text-rose-700" label="Not sent" title={c.error} testid={tid} />;
  if (!c.message_id) return <Pill cls="bg-slate-100 text-slate-400" label="Unavailable" title="No WhatsApp message ID saved for this send" testid={tid} />;
  if (ds === 'failed') return <Pill cls="bg-rose-100 text-rose-700" label="Failed" title={c.delivery_error} testid={tid} />;
  if (ds === 'delivered' || ds === 'read') return <Pill cls="bg-blue-100 text-blue-700" label="Delivered" title={fmtTs(c.delivery_ts_delivered)} testid={tid} />;
  if (ds === 'sent') return <Pill cls="bg-emerald-100 text-emerald-700" label="Sent" title="Sent by WhatsApp, delivery not confirmed yet" testid={tid} />;
  if (ds) return <Pill cls="bg-slate-100 text-slate-600" label={ds} testid={tid} />;
  return <Pill cls="bg-amber-50 text-amber-700" label="Pending" title="Waiting for WhatsApp status update" testid={tid} />;
}

function readBadge(c, i) {
  const ds = (c.delivery_status || '').toLowerCase();
  const tid = `wac-read-${i}`;
  if (ds === 'read') return <Pill cls="bg-violet-100 text-violet-700" label="Read" title={fmtTs(c.delivery_ts_read)} testid={tid} />;
  if (c.status === 'failed' || ds === 'failed') return <Pill cls="bg-slate-100 text-slate-400" label="—" testid={tid} />;
  if (!c.message_id) return <Pill cls="bg-slate-100 text-slate-400" label="Unavailable" testid={tid} />;
  if (ds === 'delivered') return <Pill cls="bg-slate-100 text-slate-600" label="Not read yet" title="No read receipt (user may have read receipts off)" testid={tid} />;
  return <Pill cls="bg-amber-50 text-amber-700" label="Pending" testid={tid} />;
}

function deliveryCounts(contacts = []) {
  const ds = contacts.map((c) => (c.delivery_status || '').toLowerCase());
  return {
    delivered: ds.filter((d) => d === 'delivered' || d === 'read').length,
    read: ds.filter((d) => d === 'read').length,
    dFailed: ds.filter((d) => d === 'failed').length,
    noId: contacts.filter((c) => c.status !== 'failed' && !c.message_id).length,
  };
}

const TONES = {
  indigo: ['bg-indigo-100', 'text-indigo-600'],
  emerald: ['bg-emerald-100', 'text-emerald-600'],
  blue: ['bg-blue-100', 'text-blue-600'],
  amber: ['bg-amber-100', 'text-amber-600'],
  rose: ['bg-rose-100', 'text-rose-600'],
  violet: ['bg-violet-100', 'text-violet-600'],
};

const Kpi = ({ icon: Icon, label, value, tone = 'indigo', testid }) => {
  const [bg, text] = TONES[tone] || TONES.indigo;
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-xl ${bg}`}><Icon className={`w-5 h-5 ${text}`} /></div>
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-xl font-bold text-slate-900">{value ?? 0}</p>
        </div>
      </div>
    </Card>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function WaCampaignPage() {
  const [kpis, setKpis] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [sending, setSending] = useState(false);

  // form state
  const [name, setName] = useState('');
  const [templateName, setTemplateName] = useState('maksappcampaign');
  const [text1, setText1] = useState('');
  const [text2, setText2] = useState('');
  const [url, setUrl] = useState('');
  const [contactsRaw, setContactsRaw] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  // detail dialog
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const parsed = parseContacts(contactsRaw);

  const fetchData = useCallback(async () => {
    setLoadingList(true);
    try {
      const [d, c] = await Promise.all([
        axios.get(`${API}/wa-campaigns/dashboard`),
        axios.get(`${API}/wa-campaigns`),
      ]);
      setKpis(d.data);
      setCampaigns(c.data.campaigns || []);
    } catch (e) {
      /* noop */
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = async (id) => {
    setLoadingDetail(true);
    try {
      const r = await axios.get(`${API}/wa-campaigns/${id}`);
      setDetailCampaign(r.data);
    } catch (e) {
      toast.error('Failed to load campaign detail');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSend = async () => {
    if (!name.trim()) { toast.error('Campaign name is required'); return; }
    if (!templateName.trim()) { toast.error('Template name is required'); return; }
    if (!text1.trim()) { toast.error('Text variable 1 is required'); return; }
    if (!text2.trim()) { toast.error('Text variable 2 is required'); return; }
    if (!url.trim()) { toast.error('URL / Link variable is required'); return; }
    if (parsed.valid.length === 0) { toast.error('No valid phone numbers found'); return; }
    setConfirmOpen(true);
  };

  const confirmSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const r = await axios.post(`${API}/wa-campaigns`, {
        name: name.trim(),
        template_name: templateName.trim(),
        text1: text1.trim(),
        text2: text2.trim(),
        url: url.trim(),
        contacts_raw: contactsRaw,
      });
      toast.success(`Campaign sent! ${r.data.sent} delivered, ${r.data.failed} failed`);
      // reset form
      setName('');
      setTemplateName('maksappcampaign');
      setText1('');
      setText2('');
      setUrl('');
      setContactsRaw('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await axios.delete(`${API}/wa-campaigns/${id}`);
      toast.success('Campaign deleted');
      fetchData();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="wa-campaigns-page">

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600">
            <Megaphone className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">WA Campaigns</h1>
            <p className="text-slate-600 text-sm">Custom WhatsApp broadcasts · Body <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">{'{{1}}'} {'{{2}}'}</code> + CTA Button URL <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">{'{{1}}'}</code></p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="wac-kpis">
          <Kpi icon={Megaphone} label="Campaigns" value={kpis?.total_campaigns} tone="indigo" testid="wac-kpi-campaigns" />
          <Kpi icon={Users} label="Total Contacts" value={kpis?.total_contacts} tone="blue" testid="wac-kpi-contacts" />
          <Kpi icon={Send} label="Messages Sent" value={kpis?.total_sent} tone="emerald" testid="wac-kpi-sent" />
          <Kpi icon={XCircle} label="Failed" value={kpis?.total_failed} tone="rose" testid="wac-kpi-failed" />
          <Kpi icon={IndianRupee} label="Cost" value={kpis?.total_cost} tone="amber" testid="wac-kpi-cost" />
        </div>

        <Tabs defaultValue="new">
          <TabsList data-testid="wac-tabs">
            <TabsTrigger value="new" data-testid="wac-tab-new">New Campaign</TabsTrigger>
            <TabsTrigger value="history" data-testid="wac-tab-history">
              Campaign History
              {campaigns.length > 0 && (
                <span className="ml-1.5 text-xs bg-emerald-500 text-white rounded-full px-1.5">{campaigns.length}</span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── NEW CAMPAIGN ─────────────────────────────────────────────── */}
          <TabsContent value="new" className="space-y-4">
            <Card className="p-5 space-y-4" data-testid="new-campaign-form">

              {/* Campaign Name + Template Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Campaign Name *</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Weekend Offer — May 2025"
                    className="mt-1"
                    data-testid="wac-name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">WhatsApp Template Name *</Label>
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="maksappcampaign"
                    className="mt-1 font-mono text-sm"
                    data-testid="wac-template-name"
                  />
                  <p className="text-xs text-slate-400 mt-0.5">Must be a Meta-approved template</p>
                </div>
              </div>

              {/* Template info banner */}
              <div className="p-3 rounded-lg bg-teal-50 border border-teal-200 text-sm text-teal-800">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span><span className="font-semibold">Template:</span>{' '}
                    <code className="bg-teal-100 px-1 rounded">{templateName || 'maksappcampaign'}</code>
                  </span>
                  <span className="text-teal-400">·</span>
                  <span>
                    <span className="font-semibold">Body:</span>{' '}
                    <code className="bg-teal-100 px-1 rounded">{'{{1}}'}</code> Text 1
                    <span className="text-teal-600 text-xs ml-1">(auto: "Dear [Name], …")</span>
                    {' '}<code className="bg-teal-100 px-1 rounded">{'{{2}}'}</code> Text 2
                  </span>
                  <span className="text-teal-400">·</span>
                  <span>
                    <span className="font-semibold">CTA Button:</span>{' '}
                    <code className="bg-teal-100 px-1 rounded">{'{{1}}'}</code> Dynamic Website URL
                    <span className="text-teal-600 text-xs ml-1">(3rd param sent to WABA)</span>
                  </span>
                </div>
              </div>

              {/* Template variables */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    <span className="inline-block bg-teal-100 text-teal-700 text-xs font-bold px-1.5 py-0.5 rounded mr-1">{'{{1}}'}</span>
                    Text Variable 1 *
                  </Label>
                  <Input
                    value={text1}
                    onChange={(e) => setText1(e.target.value)}
                    placeholder="e.g. Hi, book your pickleball slot!"
                    className="mt-1"
                    data-testid="wac-text1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    <span className="inline-block bg-teal-100 text-teal-700 text-xs font-bold px-1.5 py-0.5 rounded mr-1">{'{{2}}'}</span>
                    Text Variable 2 *
                  </Label>
                  <Input
                    value={text2}
                    onChange={(e) => setText2(e.target.value)}
                    placeholder="e.g. Use code WEEKEND20 for 20% off"
                    className="mt-1"
                    data-testid="wac-text2"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-medium text-slate-700">
                    <span className="inline-block bg-orange-100 text-orange-700 text-xs font-bold px-1.5 py-0.5 rounded mr-1">CTA Button</span>
                    Dynamic Website URL *
                    <span className="ml-1.5 text-xs text-slate-400 font-normal">(button's <code>{'{{1}}'}</code> — sent as 3rd param)</span>
                  </Label>
                  <Input
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://play.makspickleball.com"
                    className="mt-1"
                    data-testid="wac-url"
                  />
                </div>
              </div>

              {/* Message preview */}
              {(text1 || text2 || url) && (
                <div className="p-3 rounded-lg bg-slate-50 border" data-testid="wac-preview">
                  <p className="text-xs text-slate-500 mb-2 font-medium">Message Preview</p>
                  <div className="max-w-sm bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    {/* WhatsApp bubble */}
                    <div className="p-3 space-y-1">
                      {text1 && <p className="text-sm text-slate-800">{text1}</p>}
                      {text2 && <p className="text-sm text-slate-800">{text2}</p>}
                    </div>
                    {/* CTA Button */}
                    {url && (
                      <div className="border-t border-slate-100 px-3 py-2 text-center">
                        <span className="text-sm font-medium text-blue-600 flex items-center justify-center gap-1.5">
                          <MessageSquare className="w-3.5 h-3.5" />
                          <span className="truncate max-w-[220px]">{url}</span>
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">Note: <code>{'{{1}}'}</code> will be prefixed with "Dear [Name]," when contact is found</p>
                </div>
              )}

              {/* Contacts */}
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  Contact Numbers *
                  <span className="ml-2 text-xs text-slate-400 font-normal">Paste one per line, or comma / semicolon separated. Any format accepted.</span>
                </Label>
                <Textarea
                  value={contactsRaw}
                  onChange={(e) => setContactsRaw(e.target.value)}
                  rows={7}
                  placeholder={"9876543210\n919876543211\n+91 98765 43212, 8765432109"}
                  className="mt-1 font-mono text-xs"
                  data-testid="wac-contacts"
                />

                {/* Contact parse preview */}
                {contactsRaw.trim() && (
                  <div className="flex flex-wrap items-center gap-3 mt-2" data-testid="wac-contacts-preview">
                    <span className="inline-flex items-center gap-1 text-sm text-emerald-700 font-medium">
                      <CheckCircle2 className="w-4 h-4" />
                      {parsed.valid.length} valid
                    </span>
                    {parsed.invalid > 0 && (
                      <span className="inline-flex items-center gap-1 text-sm text-rose-600">
                        <XCircle className="w-4 h-4" />
                        {parsed.invalid} invalid (skipped)
                      </span>
                    )}
                    {parsed.duplicates > 0 && (
                      <span className="inline-flex items-center gap-1 text-sm text-amber-600">
                        <AlertCircle className="w-4 h-4" />
                        {parsed.duplicates} duplicates removed
                      </span>
                    )}
                    <span className="text-xs text-slate-400 ml-auto">Format: 91XXXXXXXXXX · deduplicated</span>
                  </div>
                )}
              </div>

              {/* Send button */}
              <div className="flex justify-end pt-1">
                <Button
                  onClick={handleSend}
                  disabled={sending}
                  className="bg-emerald-600 hover:bg-emerald-700 px-6"
                  data-testid="wac-send-btn"
                >
                  {sending
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                    : <><Send className="w-4 h-4 mr-2" /> Send Campaign</>
                  }
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* ── CAMPAIGN HISTORY ─────────────────────────────────────────── */}
          <TabsContent value="history">
            <Card className="p-4" data-testid="wac-history-card">
              {loadingList && campaigns.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              ) : (
                <div className="overflow-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-left text-xs text-slate-500">
                        <th className="p-3">Campaign</th>
                        <th className="p-3">Status</th>
                        <th className="p-3 text-right">Total</th>
                        <th className="p-3 text-right">Sent</th>
                        <th className="p-3 text-right">Failed</th>
                        <th className="p-3 text-right">Cost ₹</th>
                        <th className="p-3">Date</th>
                        <th className="p-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((c) => (
                        <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`wac-row-${c.id}`}>
                          <td className="p-3">
                            <div className="font-medium text-slate-900">{c.name}</div>
                            <div className="text-xs text-slate-400">{c.template_name}</div>
                          </td>
                          <td className="p-3">{statusBadge(c.status)}</td>
                          <td className="p-3 text-right font-medium">{c.total_contacts}</td>
                          <td className="p-3 text-right text-emerald-700 font-semibold">{c.sent_count}</td>
                          <td className="p-3 text-right text-rose-600">{c.failed_count}</td>
                          <td className="p-3 text-right text-slate-600">{c.cost}</td>
                          <td className="p-3 text-xs text-slate-500">{(c.created_at || '').slice(0, 10)}</td>
                          <td className="p-3 text-right whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="outline"
                              className="mr-1"
                              onClick={() => openDetail(c.id)}
                              data-testid={`wac-detail-${c.id}`}
                            >
                              <Eye className="w-3.5 h-3.5 mr-1" /> Detail
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-rose-500 hover:text-rose-700"
                              onClick={() => deleteCampaign(c.id)}
                              data-testid={`wac-delete-${c.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {campaigns.length === 0 && (
                        <tr>
                          <td colSpan={8} className="p-8 text-center text-slate-400">
                            No campaigns sent yet. Create your first campaign above.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        {/* ── CONFIRM SEND DIALOG ───────────────────────────────────────── */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="max-w-md" data-testid="wac-confirm-dialog">
            <DialogHeader>
              <DialogTitle>Confirm Campaign Send</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-slate-700">
                You are about to send the <strong>{templateName}</strong> template to{' '}
                <span className="text-emerald-700 font-bold">{parsed.valid.length} contacts</span>.
              </p>
              <div className="p-3 rounded-lg bg-slate-50 border space-y-1">
                <p><span className="font-medium text-slate-500 w-24 inline-block">Name:</span> {name}</p>
                <p><span className="font-medium text-slate-500 w-24 inline-block">Template:</span> <code className="text-xs bg-slate-100 px-1 rounded">{templateName}</code></p>
                <p><span className="font-medium text-slate-500 w-24 inline-block">{'{{1}}'}:</span> {text1} <span className="text-xs text-slate-400">(+ "Dear [Name]," if contact found)</span></p>
                <p><span className="font-medium text-slate-500 w-24 inline-block">{'{{2}}'}:</span> {text2}</p>
                <p className="break-all"><span className="font-medium text-slate-500 w-24 inline-block">CTA URL:</span> <span className="text-blue-600">{url}</span></p>
              </div>
              {parsed.invalid > 0 && (
                <p className="text-amber-600 text-xs flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> {parsed.invalid} invalid number(s) will be skipped.
                </p>
              )}
              <p className="text-xs text-slate-400">Cost: 1 credit per delivered message.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button
                onClick={confirmSend}
                disabled={sending}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="wac-confirm-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Now
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── CAMPAIGN DETAIL DIALOG ────────────────────────────────────── */}
        <Dialog open={!!detailCampaign} onOpenChange={(o) => !o && setDetailCampaign(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh]" data-testid="wac-detail-dialog">
            <DialogHeader>
              <DialogTitle>
                {detailCampaign?.name}
                <span className="ml-3">{statusBadge(detailCampaign?.status)}</span>
              </DialogTitle>
            </DialogHeader>
            {loadingDetail ? (
              <div className="flex items-center justify-center h-24 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : detailCampaign && (
              <div className="space-y-4">
                {/* Summary strip */}
                <div className="flex flex-wrap gap-3">
                  <Badge variant="secondary">Total: {detailCampaign.total_contacts}</Badge>
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Sent: {detailCampaign.sent_count}</Badge>
                  <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Failed: {detailCampaign.failed_count}</Badge>
                  <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Cost: {detailCampaign.cost}</Badge>
                  {(() => {
                    const dc = deliveryCounts(detailCampaign.contacts);
                    return (
                      <>
                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100" data-testid="wac-delivered-count">Delivered: {dc.delivered}</Badge>
                        <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100" data-testid="wac-read-count">Read: {dc.read}</Badge>
                        {dc.dFailed > 0 && <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Delivery failed: {dc.dFailed}</Badge>}
                        {dc.noId > 0 && <Badge variant="outline" className="text-slate-500" title="Sent before message tracking was added">Status unavailable: {dc.noId}</Badge>}
                      </>
                    );
                  })()}
                  <Badge variant="outline">{(detailCampaign.created_at || '').slice(0, 16).replace('T', ' ')} UTC</Badge>
                </div>

                {/* Variables */}
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="p-2 bg-teal-50 rounded border border-teal-100">
                    <span className="font-bold text-teal-700">Body {'{{1}}'}</span>
                    <p className="mt-0.5 text-slate-700 break-all">{detailCampaign.variables?.text1}</p>
                  </div>
                  <div className="p-2 bg-teal-50 rounded border border-teal-100">
                    <span className="font-bold text-teal-700">Body {'{{2}}'}</span>
                    <p className="mt-0.5 text-slate-700 break-all">{detailCampaign.variables?.text2}</p>
                  </div>
                  <div className="p-2 bg-orange-50 rounded border border-orange-100">
                    <span className="font-bold text-orange-700">CTA Button URL</span>
                    <p className="text-orange-500 text-xs italic mt-0.5">Button {'{{1}}'} · 3rd param</p>
                    <p className="mt-0.5 text-blue-600 break-all">{detailCampaign.variables?.url}</p>
                  </div>
                </div>

                {/* Per-contact table */}
                <div className="overflow-auto max-h-[45vh] border rounded-lg" data-testid="wac-contact-list">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-left text-slate-500">
                        <th className="p-2">#</th>
                        <th className="p-2">Name</th>
                        <th className="p-2">Phone</th>
                        <th className="p-2">Status</th>
                        <th className="p-2">Delivery</th>
                        <th className="p-2">Read</th>
                        <th className="p-2">Message ID</th>
                        <th className="p-2">Sent At</th>
                        <th className="p-2">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detailCampaign.contacts || []).map((c, i) => (
                        <tr key={i} className="border-t hover:bg-slate-50" data-testid={`wac-contact-row-${i}`}>
                          <td className="p-2 text-slate-400">{i + 1}</td>
                          <td className="p-2 font-medium text-slate-800">
                            {c.name
                              ? <span className="flex items-center gap-1"><Users className="w-3 h-3 text-teal-500" />{c.name}</span>
                              : <span className="text-slate-400 italic text-xs">Unknown</span>
                            }
                          </td>
                          <td className="p-2 font-mono">
                            <a href={`tel:+${c.phone}`} className="flex items-center gap-1 text-slate-700 hover:text-emerald-700">
                              <PhoneCall className="w-3 h-3" /> {c.phone}
                            </a>
                          </td>
                          <td className="p-2">{contactStatusBadge(c.status)}</td>
                          <td className="p-2">{deliveryBadge(c, i)}</td>
                          <td className="p-2">{readBadge(c, i)}</td>
                          <td className="p-2 text-slate-500 font-mono max-w-[140px] truncate" title={c.message_id}>{c.message_id || '—'}</td>
                          <td className="p-2 text-slate-400">{c.sent_at ? c.sent_at.slice(0, 16).replace('T', ' ') : '—'}</td>
                          <td className="p-2 text-rose-600 max-w-[200px] truncate" title={c.error || c.delivery_error}>{c.error || c.delivery_error || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailCampaign(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
}
